/**
 * Main App component with routing configuration
 * Includes authentication routes (login, register) and protected routes (sessions)
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LoginPage } from '@/pages/LoginPage';
import { RegisterPage } from '@/pages/RegisterPage';
import { VerifyEmailPage } from '@/pages/VerifyEmailPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { SessionsListPage } from '@/pages/SessionsListPage';
import { SessionChatPage } from '@/pages/SessionChatPage';
import { StudentDashboardPage } from '@/pages/StudentDashboardPage';
import { MathCoachGuidanceScreen } from '@/pages/MathCoachGuidanceScreen';
import { MathCoachPage } from '@/pages/MathCoachPage';
import { AuthGuard } from '@/components/AuthGuard';
// show-not-tell/TS-004 (T6) / show-not-tell/US-004 (T4): FeatureGuard + the
// gated writing-practice page. FeatureGuard composes AuthGuard (BR-TS004.1 /
// BR-008) — it is registered nested INSIDE the AuthGuard route group below.
import { FeatureGuard } from '@/components/FeatureGuard';
import { WritingPracticePage } from '@/pages/WritingPracticePage';
import { WritingHistoryPage } from '@/pages/WritingHistoryPage';
// US-006: parent wallet route — protected by ParentGuard (CCM-03)
// US-008 T5: ParentWalletPage replaced by ParentDashboardPage at ROUTES.PARENT_DASHBOARD.
//   ROUTES.PARENT_DASHBOARD stays '/parent' — no new path literal (TO-LL-013).
import { ParentGuard } from '@/components/ParentGuard';
import { ParentDashboardPage } from '@/pages/ParentDashboardPage';
// TS-017: parent-only top-up coin detail screen, opened from the dashboard coins card.
import { TopupCoinDetailPage } from '@/pages/TopupCoinDetailPage';
// TS-007 (SR6): parent-only live-sessions listing screen (US-007).
import { LiveSessionsPage } from '@/pages/LiveSessionsPage';
// Parent-only read-only transcript of a child's session, opened from the Reports tab.
import { ParentSessionViewPage } from '@/pages/ParentSessionViewPage';
// Single-source-of-truth (2026-07-02): the in-app /terms TermsPage was retired. Terms and
// Privacy now live canonically on the marketing site; the sign-up checkbox links out to them
// (TERMS_URL / PRIVACY_URL). No in-app legal-copy route remains.
// US-003 T4: import ROUTES constant so all path strings in this file reference the
// centralized constant, not hardcoded literals (enforces TO-LL-013 / TO-LL-014).
import { ROUTES } from '@/constants/routes';
// BUG-20260626-143616: group-aware boot-time root redirect (replaces unconditional
// Navigate to STUDENT_HOME at '/' and '*') - 2026-06-26
import { RootRedirect } from '@/components/RootRedirect';
// google-sign-in/US-001 (T6, AC-001.3/AC-001.5): OAuth callback page, registered
// as a PUBLIC route (outside AuthGuard) — the parent is mid-authentication when
// Cognito Hosted-UI redirects back here.
import { OAuthCallbackPage } from '@/pages/OAuthCallbackPage';
// google-sign-in/US-004 (T6): app-wide blocking consent modal, mounted once
// inside providers but OUTSIDE <Routes> so it overlays whichever page is
// active whenever the fail-closed gate (TS-004) returns 403 CONSENT_REQUIRED.
import { ConsentModal } from '@/components/ConsentModal';
// BUG-20260722-000251 (T-3, RC-001): the single app-wide error boundary.
// Mounted inside BrowserRouter but OUTSIDE <Routes> (below) so it keeps
// router/query context while catching any render throw beneath it. Do NOT
// add react-router errorElement anywhere in this file — it is a runtime
// no-op in this app's declarative (BrowserRouter + <Routes>) topology
// (RC-003); the boundary is the correction, not a supplement.
import { ErrorBoundary } from '@/components/ErrorBoundary';

// Create React Query client with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {/* BUG-20260722-000251 (T-3, RC-001): ConsentModal + Routes are wrapped in the
            single app-wide ErrorBoundary, still inside BrowserRouter so router/query
            context is retained. No route, guard, or ConsentModal placement changed —
            this is purely a containment wrapper. */}
        <ErrorBoundary>
          {/* google-sign-in/US-004 (T6): mounted once, outside <Routes>, so it
              overlays every route when consentRequired is set. */}
          <ConsentModal />
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            {/* google-sign-in/US-001 (T6): Cognito Hosted-UI Google OAuth redirect
                target. Must be public/outside AuthGuard (TO-LL-013: path referenced
                via ROUTES.OAUTH_CALLBACK, no literal). */}
            <Route path={ROUTES.OAUTH_CALLBACK} element={<OAuthCallbackPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            {/* Alias: keep /verify working for old registration email links */}
            <Route path={ROUTES.VERIFY_ALIAS} element={<Navigate to="/verify-email" replace />} />
            <Route path="/forgot-password" element={<ResetPasswordPage />} />
            {/* /reset-password alias — both paths serve the same two-step reset page */}
            <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />
            {/* Single-source-of-truth (2026-07-02): the in-app /terms route + TermsPage were
                retired. The sign-up checkbox now links directly to the canonical Terms + Privacy
                on the marketing site (TERMS_URL / PRIVACY_URL) — no embedded legal copy in-app. */}
            {/* Legacy /forgot-password/verify redirect — step 2 is now inline in ResetPasswordPage */}
            <Route
              path={ROUTES.FORGOT_PASSWORD_VERIFY}
              element={<Navigate to="/forgot-password" replace />}
            />

            {/* Protected routes */}
            <Route element={<AuthGuard />}>
              <Route path="/dashboard" element={<StudentDashboardPage />} />
              <Route path="/sessions" element={<SessionsListPage />} />
              <Route path={ROUTES.SESSION_CHAT} element={<SessionChatPage />} />
              {/* Math Coach page: shows guidance screen (no session) or chat (active session) */}
              <Route path={ROUTES.MATH_COACH} element={<MathCoachPage />} />
              {/* Legacy standalone guidance screen route — kept for backward compatibility */}
              <Route path={ROUTES.MATH_COACH_GUIDANCE} element={<MathCoachGuidanceScreen />} />
              {/* show-not-tell/TS-004 (T6) / US-004 (T4): gated writing-practice page.
                  FeatureGuard nests inside AuthGuard (auth first, entitlement second —
                  BR-TS004.1). ROUTES.WRITING_PRACTICE already exists (reserved by
                  US-003) — reused here, not re-declared (TO-LL-013). */}
              <Route element={<FeatureGuard feature="show_not_tell" />}>
                <Route path={ROUTES.WRITING_PRACTICE} element={<WritingPracticePage />} />
                {/* show-not-tell/TS-014 (T6, operability atom): registered in the
                    SAME FeatureGuard block as /writing-practice (BR-TS004.1) — the
                    "See all my writing sessions" tile link (T5) dead-ends without
                    this route. */}
                <Route path={ROUTES.WRITING_PRACTICE_HISTORY} element={<WritingHistoryPage />} />
              </Route>
            </Route>

            {/* Parent-only protected routes — ParentGuard enforces CCM-03 (US-006).
                US-003 T4: path references ROUTES.PARENT_DASHBOARD constant (TO-LL-013).
                US-008 T5: element changed from ParentWalletPage to ParentDashboardPage
                  (tabbed overview + coins + child profiles grid). Same /parent path. */}
            <Route element={<ParentGuard />}>
              <Route path={ROUTES.PARENT_DASHBOARD} element={<ParentDashboardPage />} />
              {/* TS-017 (AC-TS017.1/.4): top-up coin detail screen — inherits ParentGuard
                  protection from the shared route element above (CCM-03). */}
              <Route path={ROUTES.PARENT_TOPUP_COINS} element={<TopupCoinDetailPage />} />
              {/* TS-007 (T9, SR6, AC-TS007.4): live-sessions listing screen (US-007) —
                  inherits ParentGuard protection from the shared route element above (CCM-03). */}
              <Route path={ROUTES.PARENT_LIVE_SESSIONS} element={<LiveSessionsPage />} />
              {/* Read-only transcript of a child's session, opened from the Reports tab's
                  "View Session" button. Parent-scoped so it never renders the student chat
                  affordances (answer input, own pebbles/profile chip). */}
              <Route path={ROUTES.PARENT_SESSION} element={<ParentSessionViewPage />} />
            </Route>

            {/* BUG-20260626-143616: Root redirect — group-aware (parent->/parent,
                student/legacy->/dashboard, unauth->/login). Previously unconditionally
                Navigate to STUDENT_HOME regardless of role. */}
            <Route path={ROUTES.ROOT} element={<RootRedirect />} />

            {/* BUG-20260626-143616: Catch-all redirect — same group-aware component
                so unknown URLs (e.g. typos) also route correctly per role. */}
            <Route path="*" element={<RootRedirect />} />
          </Routes>
        </ErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
